import {
    Box,
    Button,
    Flex,
    FormControl, FormLabel,
    HStack,
    Input,
    Link, List, ListItem,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent, ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    Table,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tr,
    useColorModeValue, useDisclosure, useToast,
} from "@chakra-ui/react";
import React, {useMemo, useState} from "react";
import {
    useGlobalFilter,
    usePagination,
    useSortBy,
    useTable,
} from "react-table";
import Moment from 'moment';

// Custom components
import Card from "components/card/Card";

// Assets
import InvoiceStatus from "../invoice/InvoiceStatus";
import {MdDownload} from "react-icons/md";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import {getData, putData} from "../../services/api";
import {useForm} from "react-hook-form";
import EuropeanCountriesSelect from "../select/EuropeanCountriesSelect";

export default function ColumnsTable(props) {
    const {columnsData, tableData} = props;
    const {isOpen, onOpen, onClose} = useDisclosure()

    const toast = useToast()
    const MySwal = withReactContent(Swal)

    const columns = useMemo(() => columnsData, [columnsData]);
    const data = useMemo(() => tableData, [tableData]);

    const [invoiceSelected, setInvoiceSelected] = useState(null);
    const [loading, setLoading] = useState(false);

    const {
        handleSubmit,
        register,
        reset,
        watch,
        formState: {errors, isSubmitting},
    } = useForm()

    const initialState = {
        pageSize: 100,
        pageIndex: 0
    };

    const textColor = useColorModeValue("secondaryGray.900", "white");
    const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

    const tableInstance = useTable(
        {
            columns,
            data,
            initialState
        },
        useGlobalFilter,
        useSortBy,
        usePagination,
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: {pageIndex, pageSize},
        setGlobalFilter
    } = tableInstance;

    function onSubmit(values) {
        return new Promise(async (resolve) => {
            try {
                setLoading(true)
                await putData(`/api/invoices/${invoiceSelected.id}`, values);
                setLoading(false)
                resolve();

                onClose()

                MySwal.fire({
                    title: <p>Rechnung angepasst</p>,
                    html: <p>Die Rechnung wurde erfolgreich angepasst.</p>,
                    confirmButtonText: 'Daten aktualisieren',
                    icon: 'success',
                }).then((result) => {
                    if (result.isConfirmed) {
                        window.location.reload();
                    }
                })

            } catch (error) {
                toast({
                    title: 'Rechnung konnte nicht angepasst werden: ' + error.message,
                    status: 'error',
                    isClosable: true,
                })
                setLoading(false)
                resolve();
            }
        })
    }

    const handleShowDetails = (invoice) => {
        reset(invoice)
        setInvoiceSelected(invoice)
        onOpen()
    };

    const handleSetSearchTerm = (searchTerm) => {
        setGlobalFilter(searchTerm)
    };

    return (
        <Card
            direction='column'
            w='100%'
            px='0px'
            overflowX={{sm: "scroll", lg: "hidden"}}>

            <FormControl width={200} mb='24px' ml={'auto'} mr={5} display='flex' alignItems={'center'}>
                <FormLabel
                    ms='4px'
                    fontSize='sm'
                    fontWeight='500'
                    color={textColor}
                    htmlFor='title'
                >
                    Suche
                </FormLabel>
                <Input
                    id='title'
                    fontSize='sm'
                    type='text'
                    fontWeight='500'
                    size='lg'
                    onChange={(e) => handleSetSearchTerm(e.target.value)}
                />
            </FormControl>

            <Table {...getTableProps()} variant='simple' color='gray.500' mb='24px'>
                <Thead>
                    {headerGroups.map((headerGroup, index) => (
                        <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                            {headerGroup.headers.map((column, index) => (
                                <Th
                                    {...column.getHeaderProps(column.getSortByToggleProps())}
                                    pe='10px'
                                    key={index}
                                    borderColor={borderColor}>
                                    <Flex
                                        justify='space-between'
                                        align='center'
                                        fontSize={{sm: "10px", lg: "12px"}}
                                        color='gray.400'>
                                        {column.render("Header")}
                                    </Flex>
                                </Th>
                            ))}
                        </Tr>
                    ))}
                </Thead>
                <Tbody {...getTableBodyProps()}>
                    {page.map((row, index) => {
                        prepareRow(row);
                        return (
                            <Tr {...row.getRowProps()} key={index}>
                                {row.cells.map((cell, index) => {
                                    let data;
                                    if (cell.column.Header === "Nr.") {
                                        data = (
                                            <Button fontSize='sm' onClick={() => handleShowDetails(row.original)}>
                                                {cell.value}
                                            </Button>
                                        );
                                    } else if (cell.column.Header === "OBJEKT") {
                                        data = (
                                            <Text color={textColor} fontSize='sm' fontWeight='700'>{cell.value}</Text>
                                        );
                                    } else if (cell.column.Header === "STATUS") {
                                        data = (
                                            <Flex align='center'>
                                                <InvoiceStatus status={cell.value}/>
                                            </Flex>
                                        );
                                    } else if (cell.column.Header === "KUNDE") {
                                        data = (
                                            <Flex align='center'>
                                                <Text>{row.original.user.firstname} {row.original.user.lastname}
                                                    <br/> {row.original.user.email}</Text>
                                            </Flex>
                                        );
                                    } else if (cell.column.Header === "INSERAT") {
                                        data = (
                                            row.original.rental ?
                                                <Flex align='center'>
                                                    <Text>{row.original.rental.title} <br/> {row.original.rental.uuid}
                                                    </Text>
                                                </Flex> : ''
                                        );
                                    } else if (cell.column.Header === "BETRAG") {
                                        data = (
                                            <Flex align='center'>
                                                <Text>{cell.value} €</Text>
                                            </Flex>
                                        );
                                    } else if (cell.column.Header === "GÜLTIG BIS") {
                                        data = (
                                            <Text color={textColor} fontSize='sm' fontWeight='700'>
                                                {Moment(cell.value).format('DD.MM.YYYY HH:mm')}
                                            </Text>
                                        );
                                    } else if (cell.column.Header === "") {
                                        data = (
                                            <Box>
                                                <Link isExternal href={cell.value} title={"Rechnung herunterladen"}><MdDownload mx='2px'/></Link>
                                            </Box>
                                        );
                                    } else {
                                        data = (
                                            <Text>
                                                {cell.value}
                                            </Text>
                                        );
                                    }
                                    return (
                                        <Td
                                            {...cell.getCellProps()}
                                            key={index}
                                            fontSize={{sm: "14px"}}
                                            minW={{sm: "150px", md: "200px", lg: "auto"}}
                                            borderColor='transparent'>
                                            {data}
                                        </Td>
                                    );
                                })}
                            </Tr>
                        );
                    })}
                </Tbody>
            </Table>

            <Box className="pagination" mt={4}>
                <Flex alignItems={'center'} justifyContent="space-between" gap={{base: 5, lg: 20}}
                      direction={{base: "column", lg: 'row'}} px={5}>
                    <HStack spacing={4}>
                        <Button onClick={() => gotoPage(0)} disabled={!canPreviousPage} colorScheme="brand" size="sm">
                            {"<<"}
                        </Button>
                        <Button onClick={() => previousPage()} disabled={!canPreviousPage} colorScheme="brand"
                                size="sm">
                            {"<"}
                        </Button>
                        <Button onClick={() => nextPage()} disabled={!canNextPage} colorScheme="brand" size="sm">
                            {">"}
                        </Button>
                        <Button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage} colorScheme="brand"
                                size="sm">
                            {">>"}
                        </Button>
                    </HStack>

                    <Text>
                        <strong>
                            {pageIndex + 1} von {pageOptions.length}
                        </strong>
                    </Text>

                    <HStack spacing={4}>

                        <Text>Seite: </Text>
                        <Input
                            type="number"
                            defaultValue={pageIndex + 1}
                            onChange={(e) => {
                                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                                gotoPage(page);
                            }}
                            width="100px"
                            size="sm"
                        />
                        <Select
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                            width="fit-content"
                            size="sm"
                        >
                            {[10, 50, 100].map((pageSize) => (
                                <option key={pageSize} value={pageSize}>
                                    Zeige {pageSize}
                                </option>
                            ))}
                        </Select>
                    </HStack>


                </Flex>
            </Box>

            <Modal isOpen={isOpen} onClose={onClose}>
                <ModalOverlay/>
                <ModalContent bgColor="white">
                    <ModalHeader>Rechnung Nr. {invoiceSelected?.uuid}</ModalHeader>
                    <ModalCloseButton/>
                    <ModalBody>
                        <form>
                            <FormControl mb='24px'>
                                <FormLabel
                                    display='flex'
                                    ms='4px'
                                    fontSize='sm'
                                    fontWeight='500'
                                    color={textColor}
                                    htmlFor='status'
                                >
                                    Status
                                </FormLabel>
                                <Select
                                    id='status'
                                    fontSize='sm'
                                    type='text'
                                    fontWeight='500'
                                    size='lg'
                                    // value={statusSelected}  // bind the selected value to the state
                                    // onChange={handleChange} // handle the change event
                                    {...register('status')}
                                >
                                    <option value="pending">Zahlung ausstehend</option>
                                    <option value="paid">Gezahlt</option>
                                    <option value="unpaid">Nicht gezahlt</option>
                                </Select>
                            </FormControl>

                            <FormControl mt={5}>
                                <FormLabel
                                    display='flex'
                                    ms='4px'
                                    fontSize='sm'
                                    fontWeight='500'
                                    color={textColor}
                                    htmlFor='customer_company'
                                >
                                    Firma
                                </FormLabel>
                                <Input
                                    id='customer_company'
                                    variant='auth'
                                    fontSize='sm'
                                    type='text'
                                    fontWeight='500'
                                    size='lg'
                                    {...register('customer_company')}
                                />
                            </FormControl>

                            <Flex mt={5} gap={5}>

                                <FormControl>
                                    <FormLabel
                                        display='flex'
                                        ms='4px'
                                        fontSize='sm'
                                        fontWeight='500'
                                        color={textColor}
                                        htmlFor='customer_firstname'
                                    >
                                        Vorname
                                    </FormLabel>
                                    <Input
                                        id='customer_firstname'
                                        variant='auth'
                                        fontSize='sm'
                                        type='text'
                                        fontWeight='500'
                                        size='lg'
                                        {...register('customer_firstname')}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel
                                        display='flex'
                                        ms='4px'
                                        fontSize='sm'
                                        fontWeight='500'
                                        color={textColor}
                                        htmlFor='customer_lastname'
                                    >
                                        Nachname
                                    </FormLabel>
                                    <Input
                                        id='customer_firstname'
                                        variant='auth'
                                        fontSize='sm'
                                        type='text'
                                        fontWeight='500'
                                        size='lg'
                                        {...register('customer_lastname')}
                                    />
                                </FormControl>
                            </Flex>

                            <FormControl mt={5}>
                                <FormLabel
                                    display='flex'
                                    ms='4px'
                                    fontSize='sm'
                                    fontWeight='500'
                                    color={textColor}
                                    htmlFor='customer_email'
                                >
                                    Email
                                </FormLabel>
                                <Input
                                    id='customer_email'
                                    variant='auth'
                                    fontSize='sm'
                                    type='text'
                                    fontWeight='500'
                                    size='lg'
                                    {...register('customer_email')}
                                />
                            </FormControl>

                            <Flex mt={5} gap={5}>
                                <FormControl>
                                    <FormLabel
                                        display='flex'
                                        ms='4px'
                                        fontSize='sm'
                                        fontWeight='500'
                                        color={textColor}
                                        htmlFor='customer_street'
                                    >
                                        Straße
                                    </FormLabel>
                                    <Input
                                        id='customer_street'
                                        variant='auth'
                                        fontSize='sm'
                                        type='text'
                                        fontWeight='500'
                                        size='lg'
                                        {...register('customer_street')}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel
                                        display='flex'
                                        ms='4px'
                                        fontSize='sm'
                                        fontWeight='500'
                                        color={textColor}
                                        htmlFor='customer_street_nr'
                                    >
                                        Hausnummer
                                    </FormLabel>
                                    <Input
                                        id='customer_street_nr'
                                        variant='auth'
                                        fontSize='sm'
                                        type='text'
                                        fontWeight='500'
                                        size='lg'
                                        {...register('customer_street_nr')}
                                    />
                                </FormControl>
                            </Flex>

                            <Flex mt={5} gap={5}>
                                <FormControl>
                                    <FormLabel
                                        display='flex'
                                        ms='4px'
                                        fontSize='sm'
                                        fontWeight='500'
                                        color={textColor}
                                        htmlFor='customer_zip'
                                    >
                                        PLZ
                                    </FormLabel>
                                    <Input
                                        id='customer_zip'
                                        variant='auth'
                                        fontSize='sm'
                                        type='text'
                                        fontWeight='500'
                                        size='lg'
                                        {...register('customer_zip')}
                                    />
                                </FormControl>

                                <FormControl>
                                    <FormLabel
                                        display='flex'
                                        ms='4px'
                                        fontSize='sm'
                                        fontWeight='500'
                                        color={textColor}
                                        htmlFor='customer_place'
                                    >
                                        Ort
                                    </FormLabel>
                                    <Input
                                        id='customer_place'
                                        variant='auth'
                                        fontSize='sm'
                                        type='text'
                                        fontWeight='500'
                                        size='lg'
                                        {...register('customer_place')}
                                    />
                                </FormControl>
                            </Flex>

                            <FormControl>
                                <FormLabel
                                    display='flex'
                                    ms='4px'
                                    fontSize='sm'
                                    fontWeight='500'
                                    color={textColor}
                                    htmlFor='customer_country'
                                >
                                    Land
                                </FormLabel>
                                <EuropeanCountriesSelect
                                    register={register('customer_country', {required: 'Bitte geben Sie ein Land an'})}/>
                            </FormControl>

                            <FormControl mt={5}>
                                <FormLabel
                                    display='flex'
                                    ms='4px'
                                    fontSize='sm'
                                    fontWeight='500'
                                    color={textColor}
                                    htmlFor='customer_vatid'
                                >
                                    Umsatzsteuer-ID
                                </FormLabel>
                                <Input
                                    id='customer_vatid'
                                    variant='auth'
                                    fontSize='sm'
                                    type='text'
                                    fontWeight='500'
                                    size='lg'
                                    {...register('customer_vatid')}
                                />
                            </FormControl>
                        </form>
                        { invoiceSelected?.status !== 'paid' && watch('status') === 'paid' ? <Box
                            border="1px"
                            borderColor="red.500"
                            borderRadius="md"
                            padding="4"
                            backgroundColor="red.50"
                            mt={5}
                        >
                            <Text fontSize="sm" color="red.700">
                                Folgende Prozesse werden ausgelöst:
                                <List>
                                    <ListItem>- Aktivierung des zugehörigen Inserats</ListItem>
                                    <ListItem>- Email an Kunden zum Zahlungseingang sowie Aktivierungshinweis</ListItem>
                                </List>
                            </Text>
                        </Box> : ''}
                    </ModalBody>

                    <ModalFooter>
                        <Button variant='ghost' mr={3} onClick={onClose}>
                            Schließen
                        </Button>
                        <Button isLoading={loading} variant='brand' onClick={handleSubmit(onSubmit)}>Absenden</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Card>
    );
}
